import { useEffect, useState } from "react";
import API_BASE from "../config";
import { formatNumber } from "./phoneFormat";
import { getCached, setCached } from "./listCache";

// Line-scoped saved-contacts cache, mirroring the GET /contacts?line=... fetch
// used by the Contacts tab. Used by CallLog/Messages to decide whether a number
// is already saved (so we don't offer to create a duplicate contact silently).
export function useLineContacts(selectedNumber, authToken) {
  // Seeded from the shared per-line cache (same key the Contacts tab
  // writes) so Recents/Messages know which numbers are saved on the first
  // paint instead of showing "add" icons that flip once the fetch lands.
  const [contacts, setContacts] = useState(() => getCached("contacts", selectedNumber) || []);
  const [loaded, setLoaded] = useState(() => getCached("contacts", selectedNumber) != null);
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    if (!authToken || !selectedNumber) { setContacts([]); setLoaded(true); return undefined; }
    const cachedForLine = getCached("contacts", selectedNumber);
    setContacts(cachedForLine || []);
    setLoaded(cachedForLine != null);
    const ctl = new AbortController();
    (async () => {
      try {
        const opts = {
          headers: { Authorization: `Bearer ${authToken}`, "ngrok-skip-browser-warning": "true" },
          signal: ctl.signal,
        };
        const res = await fetch(`${API_BASE}/contacts?line=${encodeURIComponent(selectedNumber)}`, opts);
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (!ctl.signal.aborted) {
          if (Array.isArray(data)) {
            setContacts(data);
            setCached("contacts", selectedNumber, data);
          }
          setLoaded(true);
        }
      } catch (e) {
        if (e.name !== "AbortError") setLoaded(true);
      }
    })();
    return () => ctl.abort();
  }, [selectedNumber, authToken, reloadTick]);
  return [contacts, () => setReloadTick((t) => t + 1), loaded];
}

// Matches a raw number against a contact's primary or any extra number.
// Both sides are normalized to canonical E.164 by the shared formatNumber()
// (see ./phoneFormat.js), so lookup "7135551234" matches a stored
// "+17135551234" and vice versa.
export function isNumberSaved(contacts, number) {
  // formatNumber("") / formatNumber(null) both return the truthy "+" (no
  // digits to normalize), so a blank/missing input has to be rejected
  // before normalizing -- otherwise it would wrongly "match" any contact
  // whose own stored number is also blank.
  if (!String(number || "").replace(/\D/g, "")) return false;
  const target = formatNumber(number);
  return (contacts || []).some((c) => {
    const nums = [
      formatNumber(c && c.number),
      ...(c && c.numbers || []).map((n) => formatNumber(n && n.number)),
    ];
    return nums.includes(target);
  });
}
// Like isNumberSaved but returns the matching contact record (or null). Used
// to read a saved contact's translate_lang when resolving the remote language
// of a call or SMS thread with `number` on the current line.
export function findContactByNumber(contacts, number) {
  if (!String(number || "").replace(/\D/g, "")) return null;
  const target = formatNumber(number);
  return (contacts || []).find((c) => {
    const nums = [
      formatNumber(c && c.number),
      ...(c && c.numbers || []).map((n) => formatNumber(n && n.number)),
    ];
    return nums.includes(target);
  }) || null;
}

// Saved name for a number ("First Last", else company), or null when the
// number isn't saved or the contact has no name. Lets Messages/Recents show
// who someone is instead of only their raw number.
export function savedContactName(contacts, number) {
  const c = findContactByNumber(contacts, number);
  if (!c) return null;
  const full = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return full || c.company || null;
}
